// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppIcon } from '@shared/ui-kit/components/app-icon';

export type SelectorProps<T> = {
  options?: T[];
  optionKey: keyof T;
  valueKey: keyof T;
  placeholder: string;
  liveSearch?: boolean;
  noOptionsText?: string;
  groupBy?: (option: T) => string;
  onSelectValue?: (value: T) => void;
  sourceData?: (searchText?: string) => Promise<T[]>;
  value?: T | null;
  isClearable?: boolean;
};

const debounce = <T extends (...args: any[]) => void>(func: T, wait: number): T => {
  let timeoutId: ReturnType<typeof setTimeout>;

  return function (...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeoutId);
      func(...args);
    };
    clearTimeout(timeoutId);
    timeoutId = setTimeout(later, wait);
  } as T;
};

export const Selector = <T,>({
  options = [],
  groupBy,
  optionKey,
  valueKey,
  onSelectValue,
  placeholder,
  sourceData,
  liveSearch = false,
  noOptionsText,
  value,
  isClearable = false,
}: SelectorProps<T>) => {
  const [open, setOpen] = useState(false);
  const [listOptions, setListOptions] = useState<T[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const runSafeUpdate = useSafeUpdate();

  const toggleOpen = useCallback(() => setOpen(prev => !prev), []);

  const handleOptionValue = useCallback(
    (option: any, currentValue: any) => {
      /* istanbul ignore if */
      if (!currentValue) return false;
      return option[valueKey] === currentValue[valueKey];
    },
    [valueKey],
  );

  const handleOptionLabel = useCallback(
    (option: any) => {
      /* istanbul ignore if */
      if (typeof option !== 'object' || !option) return '';
      return option[optionKey];
    },
    [optionKey],
  );

  const handleInputChange = useCallback(
    (_: any, newValue: string) => {
      if (!newValue && liveSearch) {
        setListOptions([]);
      }
      setInputValue(newValue);
    },
    [liveSearch],
  );

  const handleSelectValue = useCallback(
    (_: any, newValue: any) => {
      /* istanbul ignore next */
      setInputValue(newValue ? String(newValue[optionKey]) : '');
      if (onSelectValue) onSelectValue(newValue);
    },
    [onSelectValue, optionKey],
  );

  const handleFilterOptions = useCallback(
    (options: any, { inputValue }: any) => {
      if (!inputValue) return options;
      return options.filter((option: any) => {
        const label = option[optionKey];
        return label.toLowerCase().includes(inputValue.toLowerCase());
      });
    },
    [optionKey],
  );

  const fetchOptions = useMemo(
    () =>
      debounce(async (inputValue?: string) => {
        if (!sourceData) return;
        try {
          runSafeUpdate(() => setIsLoading(true));
          const response = await sourceData(inputValue);
          runSafeUpdate(() => setListOptions(response));
        } catch (error) {
          /* istanbul ignore next */
          console.error('Failed to fetch data:', error);
        } finally {
          runSafeUpdate(() => setIsLoading(false));
        }
      }, 300),
    [runSafeUpdate, sourceData],
  );

  useEffect(() => {
    if (inputValue && open && liveSearch) {
      fetchOptions(inputValue);
    }
  }, [inputValue, open, liveSearch, fetchOptions]);

  useEffect(() => {
    if (options.length > 0) {
      setListOptions(() => options);
    }
  }, [options]);

  useEffect(() => {
    fetchOptions();
  }, [sourceData, fetchOptions]);

  return (
    <Autocomplete
      data-testid="selector"
      groupBy={groupBy}
      open={open}
      value={value}
      onOpen={toggleOpen}
      onClose={toggleOpen}
      onChange={handleSelectValue}
      disablePortal
      fullWidth
      getOptionLabel={handleOptionLabel}
      isOptionEqualToValue={handleOptionValue}
      options={listOptions}
      onInputChange={handleInputChange}
      filterOptions={handleFilterOptions}
      noOptionsText={noOptionsText}
      popupIcon={<AppIcon icon="ArrowDown" className="text-border-medium" />}
      disableClearable={!isClearable}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            classes: { input: '!placeholder-primary-medium/70' },
            disableUnderline: true,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress size={20} data-testid="selector-loading" />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          size="small"
          variant="standard"
          type="text"
        />
      )}
      sx={{
        '& .MuiAutocomplete-popupIndicator': {
          marginRight: 1,
        },
        '& .MuiAutocomplete-clearIndicator': {
          color: 'var(--border-medium)',
        },
      }}
    />
  );
};
